import React from 'react';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';

const formatBytes = (bytes) => {
  const value = Number(bytes);
  if (!Number.isFinite(value) || value <= 0) return '0 B';

  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const unitIndex = Math.min(Math.floor(Math.log(value) / Math.log(1024)), units.length - 1);
  const amount = value / Math.pow(1024, unitIndex);
  const formatted = unitIndex === 0 ? Math.round(amount) : parseFloat(amount.toFixed(1));

  return `${formatted} ${units[unitIndex]}`;
};

const BENEFITS = [
  {
    title: 'Longer link retention',
    description: 'Keep files available while your plan is active.'
  },
  {
    title: '100 GB to 1 TB storage',
    description: 'Basic 100 GB · Pro 500 GB · Premium 1 TB.'
  },
  {
    title: 'Share without download fees',
    description: 'Unlimited bandwidth and in-browser video playback.'
  }
];

const StorageQuotaModal = ({ isOpen, onClose, quotaDetails, onUpgrade }) => {
  const navigate = useNavigate();

  if (!isOpen) return null;

  const usageValue = Number(quotaDetails?.currentStorageUsage);
  const currentUsage = Number.isFinite(usageValue) && usageValue > 0 ? usageValue : 0;
  const quotaValue = Number(quotaDetails?.storageQuota);
  const storageQuota = Number.isFinite(quotaValue) && quotaValue > 0
    ? quotaValue
    : 10737418240;
  const usagePercent = Math.min(100, Math.round((currentUsage / storageQuota) * 100));
  const isStorageFull = currentUsage >= storageQuota;

  const handleClose = () => {
    if (onClose) onClose();
  };

  const handleUpgradeClick = () => {
    handleClose();
    if (onUpgrade) {
      onUpgrade();
    } else {
      navigate('/pricing');
    }
  };

  const handleManageFilesClick = () => {
    handleClose();
    navigate('/my-files');
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.78)',
        backdropFilter: 'blur(6px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 11000,
        padding: '16px',
        fontFamily: "'Largetransfer', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
      }}
      onClick={handleClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="storage-quota-title"
        aria-describedby="storage-quota-description"
        style={{
          backgroundColor: '#12141a',
          border: '1px solid #282d38',
          borderRadius: '12px',
          padding: '24px',
          maxWidth: '440px',
          width: '100%',
          maxHeight: 'calc(100vh - 32px)',
          overflowY: 'auto',
          position: 'relative',
          boxShadow: '0 24px 70px rgba(0, 0, 0, 0.55)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={handleClose}
          aria-label="Close storage details"
          style={{
            position: 'absolute',
            top: '14px',
            right: '14px',
            width: '32px',
            height: '32px',
            backgroundColor: 'transparent',
            border: '1px solid transparent',
            color: '#8b909c',
            cursor: 'pointer',
            padding: 0,
            borderRadius: '6px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'color 0.15s, background-color 0.15s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = '#ffffff';
            e.currentTarget.style.backgroundColor = '#1b1e27';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = '#8b909c';
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          <X size={17} />
        </button>

        <div style={{ paddingRight: '34px', marginBottom: '18px' }}>
          <div style={{
            color: '#9298a5',
            fontSize: '10px',
            fontWeight: '600',
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            marginBottom: '8px'
          }}>
            {isStorageFull ? 'Storage full' : 'Free plan'}
          </div>
          <h2
            id="storage-quota-title"
            style={{
              fontSize: '20px',
              lineHeight: 1.25,
              fontWeight: '600',
              color: '#ffffff',
              margin: '0 0 8px',
              letterSpacing: '-0.025em'
            }}
          >
            {isStorageFull ? 'Free storage limit reached' : 'Storage and plan details'}
          </h2>
          <p
            id="storage-quota-description"
            style={{
              fontSize: '13px',
              color: '#a0a5b0',
              margin: 0,
              lineHeight: 1.55
            }}
          >
            {isStorageFull
              ? `You’ve used your ${formatBytes(storageQuota)} free storage. Delete files to upload more, or upgrade for more space and longer retention.`
              : `The free plan includes up to ${formatBytes(storageQuota)} of active storage and 24-hour links. Upgrade for more space and longer retention.`}
          </p>
        </div>

        <div style={{
          backgroundColor: '#181a22',
          borderRadius: '8px',
          padding: '14px',
          border: '1px solid #252a35',
          marginBottom: '18px'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            gap: '12px',
            marginBottom: '10px'
          }}>
            <span style={{ color: '#a0a5b0', fontSize: '12px' }}>Storage used</span>
            <span style={{
              color: '#f4f5f7',
              fontSize: '12px',
              fontWeight: '600',
              whiteSpace: 'nowrap'
            }}>
              {formatBytes(currentUsage)} / {formatBytes(storageQuota)}
            </span>
          </div>
          <div
            role="progressbar"
            aria-label="Storage used"
            aria-valuemin="0"
            aria-valuemax="100"
            aria-valuenow={usagePercent}
            style={{
              width: '100%',
              height: '5px',
              backgroundColor: '#0e1015',
              borderRadius: '999px',
              overflow: 'hidden'
            }}
          >
            <div style={{
              width: usagePercent + '%',
              height: '100%',
              backgroundColor: isStorageFull ? '#f87171' : '#c8ccd4',
              borderRadius: '999px',
              transition: 'width 0.3s ease'
            }} />
          </div>
          <div style={{
            marginTop: '9px',
            color: '#7f8592',
            fontSize: '11px',
            lineHeight: 1.4
          }}>
            Free plan · Links expire after 24 hours
          </div>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
            marginBottom: '4px'
          }}>
            <h3 style={{
              color: '#f1f2f4',
              fontSize: '13px',
              fontWeight: '600',
              margin: 0
            }}>
              Upgrade benefits
            </h3>
            <span style={{ color: '#9096a2', fontSize: '11px', whiteSpace: 'nowrap' }}>
              Basic · $5/mo
            </span>
          </div>
          {BENEFITS.map((benefit, index) => (
            <div
              key={benefit.title}
              style={{
                padding: '10px 0',
                borderBottom: index < BENEFITS.length - 1 ? '1px solid #242833' : 'none'
              }}
            >
              <div style={{
                color: '#e6e8ec',
                fontSize: '12px',
                fontWeight: '600',
                marginBottom: '3px'
              }}>
                {benefit.title}
              </div>
              <div style={{ color: '#9298a5', fontSize: '12px', lineHeight: 1.45 }}>
                {benefit.description}
              </div>
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '9px' }}>
          <button
            type="button"
            onClick={handleUpgradeClick}
            style={{
              backgroundColor: '#ffffff',
              color: '#101115',
              padding: '11px 16px',
              borderRadius: '7px',
              border: '1px solid #ffffff',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'background-color 0.15s ease',
              width: '100%'
            }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#e7e8eb'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#ffffff'; }}
          >
            Upgrade to Basic · $5/mo
          </button>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button
              type="button"
              onClick={handleManageFilesClick}
              style={{
                flex: 1,
                backgroundColor: '#1b1e27',
                color: '#e4e6ea',
                border: '1px solid #2b303d',
                padding: '9px 12px',
                borderRadius: '7px',
                fontSize: '12px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'background-color 0.15s ease, border-color 0.15s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#222632';
                e.currentTarget.style.borderColor = '#3a4050';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#1b1e27';
                e.currentTarget.style.borderColor = '#2b303d';
              }}
            >
              Manage My Files
            </button>

            <button
              type="button"
              onClick={handleClose}
              style={{
                backgroundColor: 'transparent',
                color: '#8b909c',
                border: '1px solid transparent',
                padding: '9px 12px',
                borderRadius: '7px',
                fontSize: '12px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'color 0.15s ease'
              }}
              onMouseEnter={(e) => { e.currentTarget.style.color = '#ffffff'; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = '#8b909c'; }}
            >
              Dismiss
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StorageQuotaModal;
